import {
  BarChart3,
  Clock,
  HelpCircle,
  Calendar,
  Wrench,
  LayoutDashboard,
  Settings2,
  ChevronRight,
  ShieldCheck,
  AlertTriangle
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import LoadingSpinner from '@/components/LoadingSpinner';
import { supabase } from '@/lib/supabase';
import { useQuery } from '@tanstack/react-query';

// Tipos
interface UserMachine {
  id: number;
  machineId: number;
  machineName: string;
  purchaseDate: string;
  status: 'operational' | 'maintenance' | 'warning';
  lastMaintenance: string;
  nextMaintenance: string;
  health: number;
}

interface DashboardOverviewProps {
  userData: any;
}

// Obtener máquinas del usuario desde Supabase
const fetchUserMachines = async (userId: string): Promise<UserMachine[]> => {
  if (!userId) {
    throw new Error("ID de usuario no disponible");
  }

  try {
    console.log("Obteniendo máquinas para el usuario:", userId);

    // Primero obtenemos las máquinas asignadas al usuario desde la tabla user_machines
    const { data: userMachinesData, error: userMachinesError } = await supabase
      .from('user_machines')
      .select('*')
      .eq('user_id', userId)
      .order('id', { ascending: false });

    if (userMachinesError) {
      console.error("Error al obtener las máquinas del usuario:", userMachinesError);
      throw userMachinesError;
    }

    if (!userMachinesData || userMachinesData.length === 0) {
      console.log("No se encontraron máquinas para el usuario", userId);
      return [];
    }

    console.log("Máquinas encontradas:", userMachinesData.length);

    // Preparamos las máquinas con datos de mantenimiento
    const machines: UserMachine[] = userMachinesData.map((machine: any) => {
      // Calcular fechas de mantenimiento basadas en la fecha de compra
      const purchaseDate = new Date(machine.purchase_date);

      // Última fecha de mantenimiento (simulada: 3 meses después de la compra)
      const lastMaintenanceDate = new Date(purchaseDate);
      lastMaintenanceDate.setMonth(purchaseDate.getMonth() + 3);

      // Próxima fecha de mantenimiento (simulada: 6 meses después de la compra)
      const nextMaintenanceDate = new Date(purchaseDate);
      nextMaintenanceDate.setMonth(purchaseDate.getMonth() + 6);

      // Calcular salud de la máquina de manera dinámica (entre 60-100%)
      const today = new Date();
      const daysSinceLastMaintenance = Math.max(0, Math.floor((today.getTime() - lastMaintenanceDate.getTime()) / (1000 * 60 * 60 * 24)));

      // La salud disminuye con el tiempo desde el último mantenimiento
      const health = Math.max(60, Math.min(100, 100 - Math.floor(daysSinceLastMaintenance / 30) * 5));

      // Estado de la máquina basado en su salud
      let status: 'operational' | 'maintenance' | 'warning' = 'operational';
      if (health < 70) {
        status = 'warning';
      } else if (nextMaintenanceDate.getTime() < today.getTime()) {
        status = 'maintenance';
      }

      return {
        id: machine.id,
        machineId: machine.machine_id,
        machineName: machine.machine_name,
        purchaseDate: machine.purchase_date,
        status: status,
        lastMaintenance: lastMaintenanceDate.toISOString(),
        nextMaintenance: nextMaintenanceDate.toISOString(),
        health: health
      };
    });

    return machines;
  } catch (error) {
    console.error("Error al obtener las máquinas del usuario:", error);
    // No devolver datos de ejemplo en caso de error
    return [];
  }
};

export default function DashboardOverview({ userData }: DashboardOverviewProps) {
  // Usar React Query para obtener las máquinas
  const {
    data: machines = [],
    isLoading,
    error
  } = useQuery({
    queryKey: ['userMachines', userData?.id],
    queryFn: () => fetchUserMachines(userData?.id),
    enabled: !!userData?.id,
    staleTime: 5 * 60 * 1000, // 5 minutos
  });

  // Formatear fecha
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(date);
  };

  // Obtener el estado de cada máquina
  const getMachineStatusInfo = (status: string) => {
    switch (status) {
      case 'operational':
        return {
          label: 'Operativa',
          color: 'bg-emerald-500',
          textColor: 'text-emerald-500',
          icon: <ShieldCheck className="h-4 w-4" />
        };
      case 'maintenance':
        return {
          label: 'En mantenimiento',
          color: 'bg-blue-500',
          textColor: 'text-blue-500',
          icon: <Wrench className="h-4 w-4" />
        };
      case 'warning':
        return {
          label: 'Necesita revisión',
          color: 'bg-amber-500',
          textColor: 'text-amber-500',
          icon: <AlertTriangle className="h-4 w-4" />
        };
      default:
        return {
          label: 'Desconocido',
          color: 'bg-gray-500',
          textColor: 'text-gray-500',
          icon: <HelpCircle className="h-4 w-4" />
        };
    }
  };

  // Obtener el nombre del usuario
  const getUserName = () => {
    return userData?.user_metadata?.name || 'Usuario';
  };

  // Contar máquinas por estado
  const getMachineCountByStatus = () => {
    if (machines.length === 0) {
      return {
        operational: 0,
        maintenance: 0,
        warning: 0,
        total: 0
      };
    }

    return {
      operational: machines.filter(m => m.status === 'operational').length,
      maintenance: machines.filter(m => m.status === 'maintenance').length,
      warning: machines.filter(m => m.status === 'warning').length,
      total: machines.length
    };
  };

  const machineStats = getMachineCountByStatus();

  // Render pantalla de carga
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[400px]">
        <LoadingSpinner />
      </div>
    );
  }

  // Render estado de error
  if (error) {
    return (
      <div className="space-y-8">
        <div>
          <h1 className="text-2xl font-bold text-white mb-2">
            ¡Bienvenido, {getUserName()}!
          </h1>
          <p className="text-zinc-400">
            Ha ocurrido un error al cargar tus máquinas.
          </p>
        </div>

        <div className="bg-red-900/20 border border-red-800/50 rounded-xl p-8 text-center">
          <AlertTriangle className="h-12 w-12 mx-auto text-red-500 mb-2" />
          <h2 className="text-xl font-semibold text-white mb-2">Error al cargar datos</h2>
          <p className="text-zinc-400 max-w-md mx-auto mb-6">
            No pudimos cargar la información de tus máquinas. Por favor, intenta recargar la página o contacta con soporte técnico.
          </p>
          <Button
            variant="outline"
            className="border-zinc-700 hover:bg-zinc-700 text-white"
            onClick={() => window.location.reload()}
          >
            Reintentar
          </Button>
        </div>
      </div>
    );
  }

  // Render estado sin máquinas
  if (machines.length === 0) {
    return (
      <div className="space-y-8">
        <div>
          <h1 className="text-2xl font-bold text-white mb-2">
            ¡Bienvenido, {getUserName()}!
          </h1>
          <p className="text-zinc-400">
            Aún no tienes máquinas registradas en tu cuenta.
          </p>
        </div>

        <div className="bg-zinc-800/50 border border-zinc-700/50 rounded-xl p-8 text-center">
          <div className="mx-auto w-16 h-16 bg-zinc-700/50 rounded-full flex items-center justify-center mb-4">
            <HelpCircle className="h-8 w-8 text-zinc-500" />
          </div>
          <h2 className="text-xl font-semibold text-white mb-2">No hay máquinas disponibles</h2>
          <p className="text-zinc-400 max-w-md mx-auto mb-6">
            Todavía no tienes máquinas asignadas a tu cuenta. Contacta con tu administrador o representante de ventas para que asigne tus máquinas.
          </p>
          <Button
            variant="outline"
            className="border-zinc-700 hover:bg-zinc-700 text-white"
          >
            Contactar con soporte
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Encabezado con saludo */}
      <div>
        <h1 className="text-2xl font-bold text-white mb-2">
          ¡Bienvenido, {getUserName()}!
        </h1>
        <p className="text-zinc-400">
          Aquí tienes un resumen de tus máquinas y su estado actual.
        </p>
      </div>

      {/* Estadísticas generales */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-zinc-800/50 border-zinc-700/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-zinc-400">Total de máquinas</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-baseline space-x-2">
              <span className="text-3xl font-bold text-white">{machineStats.total}</span>
              <span className="text-sm text-zinc-500">unidades</span>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-zinc-800/50 border-zinc-700/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-zinc-400">Última revisión</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-baseline space-x-2">
              <span className="text-3xl font-bold text-white">
                {machines.length > 0 ? formatDate(machines[0].lastMaintenance).split('/').slice(0, 2).join('/') : '-'}
              </span>
              <span className="text-sm text-zinc-500">
                {machines.length > 0 ? formatDate(machines[0].lastMaintenance).split('/')[2] : ''}
              </span>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-zinc-800/50 border-zinc-700/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-zinc-400">Próximo mantenimiento</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-baseline space-x-2">
              <span className="text-3xl font-bold text-white">
                {machines.length > 0 ? formatDate(machines[0].nextMaintenance).split('/').slice(0, 2).join('/') : '-'}
              </span>
              <span className="text-sm text-zinc-500">
                {machines.length > 0 ? formatDate(machines[0].nextMaintenance).split('/')[2] : ''}
              </span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Distribución de estados */}
      <Card className="bg-zinc-800/50 border-zinc-700/50">
        <CardHeader>
          <CardTitle className="text-white">Estado de tus máquinas</CardTitle>
          <CardDescription>Distribución por estado operacional</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center gap-2 p-3 bg-zinc-800/80 rounded-lg">
              <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
              <div className="flex-1">
                <div className="text-sm text-white">Operativas</div>
                <div className="text-xs text-zinc-400">{machineStats.operational} máquinas</div>
              </div>
              <div className="text-right">
                <div className="text-xl font-bold text-white">
                  {machineStats.total > 0 ? Math.round((machineStats.operational / machineStats.total) * 100) : 0}%
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 p-3 bg-zinc-800/80 rounded-lg">
              <div className="w-3 h-3 rounded-full bg-blue-500"></div>
              <div className="flex-1">
                <div className="text-sm text-white">En mantenimiento</div>
                <div className="text-xs text-zinc-400">{machineStats.maintenance} máquinas</div>
              </div>
              <div className="text-right">
                <div className="text-xl font-bold text-white">
                  {machineStats.total > 0 ? Math.round((machineStats.maintenance / machineStats.total) * 100) : 0}%
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 p-3 bg-zinc-800/80 rounded-lg">
              <div className="w-3 h-3 rounded-full bg-amber-500"></div>
              <div className="flex-1">
                <div className="text-sm text-white">Necesitan revisión</div>
                <div className="text-xs text-zinc-400">{machineStats.warning} máquinas</div>
              </div>
              <div className="text-right">
                <div className="text-xl font-bold text-white">
                  {machineStats.total > 0 ? Math.round((machineStats.warning / machineStats.total) * 100) : 0}%
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Lista de máquinas */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-white">Tus máquinas</h2>
          <Button variant="outline" size="sm" className="text-zinc-400 border-zinc-700/50 hover:bg-zinc-800 hover:text-white">
            Ver todas
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>

        <div className="space-y-4">
          {machines.map((machine) => {
            const status = getMachineStatusInfo(machine.status);

            return (
              <div
                key={machine.id}
                className="p-4 bg-zinc-800/50 rounded-lg border border-zinc-700/50 hover:border-[#FCAA0D]/30 transition-colors"
              >
                <div className="flex flex-col md:flex-row md:items-center gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="font-medium text-white">{machine.machineName}</h3>
                      <Badge
                        variant="outline"
                        className={`${status.textColor} border-${status.color}/30 bg-${status.color}/10`}
                      >
                        <span className="flex items-center gap-1">
                          {status.icon}
                          {status.label}
                        </span>
                      </Badge>
                    </div>

                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="text-zinc-400">
                        <span className="text-zinc-500">Fecha compra:</span> {formatDate(machine.purchaseDate)}
                      </div>
                      <div className="text-zinc-400">
                        <span className="text-zinc-500">Última revisión:</span> {formatDate(machine.lastMaintenance)}
                      </div>
                      <div className="text-zinc-400">
                        <span className="text-zinc-500">Próximo mantenimiento:</span> {formatDate(machine.nextMaintenance)}
                      </div>
                      <div className="text-zinc-400">
                        <span className="text-zinc-500">ID:</span> {machine.machineId}
                      </div>
                    </div>
                  </div>

                  <div className="md:w-[180px] space-y-2">
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="text-zinc-400">Estado de salud</span>
                      <span className={machine.health > 80 ? 'text-emerald-400' : machine.health > 50 ? 'text-amber-400' : 'text-red-400'}>
                        {machine.health}%
                      </span>
                    </div>
                    <Progress
                      value={machine.health}
                      className="h-2 bg-zinc-700"
                      style={{
                        '--progress-foreground': machine.health > 80
                          ? 'hsl(142, 76%, 36%)' // emerald-500
                          : machine.health > 50
                            ? 'hsl(43, 96%, 58%)' // amber-500
                            : 'hsl(0, 84%, 60%)' // red-500
                      } as React.CSSProperties}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
